/* eslint-disable @next/next/no-img-element */
import { Headline4, StandardHeadline, StandardSubtitle } from 'stories/elements/Typography/Text'
import styled, { css } from 'styled-components'
import theme from 'stories/utils/theme'
import IconDark from 'assets/footer/knowunity-logo.png'
import Link from 'next/link'
import useMedia from 'lib/hooks/useMedia'
import { deviceSize } from 'lib/constants/GridBreakpoints'
import FeatureNotAvailable from 'components/feature/App/pages/FeatureNotAvailable'
import Meta from 'components/modules/SEO/Meta'
import GetInTouchSection from 'components/feature/App/widgets/GetInTouchSection'
import LegalPagesFooter from 'components/feature/App/navigation/LegalPagesFooter'
import AppHeader from 'components/feature/App/navigation/AppHeader'

const MobileContainer = styled.section`
    .logo {
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 7px;
    }
    @media (min-width: 1051px) {
        display: none;
    }
`

const Container = styled.div`
    width: 100%;
    min-height: 100vh;
    display: flex;
    position: relative;
    background: transparent;
    z-index: 1;
    &::after {
        content: '';
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100vh;
        background: linear-gradient(180.02deg, #150620 0.01%, #14183a 50%, #150620 99.99%);
        z-index: -1;
        pointer-events: none;
    }

    .section-header {
        height: 50px;
        display: flex;
        align-items: center;
    }
    @media (max-width: 1050px) {
        display: none;
    }
`

const NavigationSidebar = styled.header`
    position: fixed;
    left: 0;
    top: 0;
    flex-grow: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    justify-content: start;
    background: #ffffff1a;
    padding: 25px 0 25px 50px;
    z-index: ${theme.zIndex.navBar + 2};
    transition: all 0.3s ease;
    width: 65px;
    height: 100vh;
    overflow: hidden;

    width: 250px;
    .logo {
        justify-content: flex-start;
        padding-left: 20px;
    }

    .text-label {
        opacity: 1;
    }

    .logo {
        width: 250px;
        min-width: 250px;
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 7px;
        transition: all 0.3s ease;
    }

    .text-label {
        opacity: 0;
        transition: opacity 0.3s ease;
        white-space: nowrap;
    }

    .content {
        width: 250px;
        min-width: 250px;
        transition: all 0.3s ease;
    }

    .sticky {
        position: fixed;
        top: 35px;
        height: calc(100vh - 70px);
        overflow-y: auto;
        overflow-x: hidden;
        width: inherit;
    }

    nav {
        width: 250px;
        min-width: 250px;
    }
    @media (max-width: 1250px) {
        width: 125px;
        nav {
            width: 125px !important;
            min-width: 125px !important;
        }
    }
`

const Main = styled.main<{ hidePrimarySection?: boolean; hideSecondarySection?: boolean }>`
    margin-left: 50px;
    padding-right: 125px;
    padding-top: 70px;
    flex: 1;
    display: flex;
    justify-content: center;
    transition: margin-left 0.3s ease;

    @media (max-width: 1250px) {
        margin-left: 25px;
    }

    ${({ hideSecondarySection }) =>
        hideSecondarySection
            ? css`
                  margin: 0px;
                  padding: 0;
                  overflow: hidden;
              `
            : ''}

    h1,
    h2 {
        margin: 0;
    }

    .primary-section {
        width: 650px;
        flex: none;
        background-color: ${theme.colors.transparent};
        display: grid;
        place-content: start;
        padding: 35px 20px;
        gap: 30px;

        @media (max-width: 1250px) {
            width: 750px;
        }

        .primary-section-content {
            width: 100%;
        }

        .section-header {
            width: 100%;
            height: ${({ hidePrimarySection }) => (hidePrimarySection ? 0 : 50)}px;
        }
        ${({ hideSecondarySection }) =>
            hideSecondarySection
                ? css`
                      padding: 0px;
                  `
                : ''}
    }

    .secondary-section {
        position: fixed;
        right: 0;
        top: 0;
        width: 360px;
        max-width: 360px;
        height: 100vh;
        overflow-y: auto;
        overflow-x: hidden;
        border-left: 1px solid #ffffff26;
        background: linear-gradient(180.02deg, #150620 0.01%, #14183a 50%, #150620 99.99%);
        padding: 0px 20px 35px;
        z-index: ${theme.zIndex.tooltipText};

        .secondary-content {
            padding-bottom: 50px;
        }

        .section-header {
            height: 20px;
            width: 320px !important;
            max-width: 320px !important;
        }
    }
`

const Wrapper = styled.div`
    background-color: ${theme.colors.white};
    height: 100vh;
    .header-spacer {
        height: 75px;
    }
    @media (max-width: 1050px) {
        background: linear-gradient(180.02deg, #150620 0.01%, #14183a 50%, #150620 99.99%);
    }
`

interface Props {
    primarySectionTitle: string
    secondarySectionTitle: string
    primaryComponent: JSX.Element
    secondaryComponent: JSX.Element
    navigationComponent: JSX.Element
    headerTitle?: string | JSX.Element
    showSearch?: boolean
    showBackButton?: boolean
    hideSecondarySection?: boolean
    headerComponent?: JSX.Element
}

const AppContainer = ({
    primarySectionTitle,
    secondarySectionTitle,
    primaryComponent,
    secondaryComponent,
    navigationComponent,
    headerTitle,
    showSearch,
    showBackButton,
    hideSecondarySection,
    headerComponent,
}: Props) => {
    const lg = useMedia(deviceSize.tablet)

    const isPrimarySectionPresent = !!primarySectionTitle?.length

    return (
        <Wrapper>
            <Meta title="Knowunity" description="Knowunity" />
            <Container>
                <NavigationSidebar>
                    <div className="sticky">
                        <div className="content">
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    paddingLeft: !lg ? '30px' : '57.5px',
                                }}
                            >
                                <img src={IconDark.src} alt="Knowunity Logo" width={19} height={26} />
                                {!lg ? (
                                    <Headline4 color={theme.colors.white} fontFamily="var(--font-inter)">
                                        Knowunity
                                    </Headline4>
                                ) : null}
                            </div>
                            {navigationComponent}
                        </div>
                    </div>
                </NavigationSidebar>
                <AppHeader
                    title={headerTitle}
                    showSearch={showSearch}
                    showBackButton={showBackButton}
                    hideSecondarySection={hideSecondarySection}
                    headerComponent={headerComponent}
                />
                <Main role="main" hidePrimarySection={!isPrimarySectionPresent} hideSecondarySection={hideSecondarySection}>
                    {!hideSecondarySection ? (
                        <div className="primary-section">
                            <div className="primary-section-content">
                                <div className="section-header">
                                    {isPrimarySectionPresent ? (
                                        <StandardHeadline>{primarySectionTitle}</StandardHeadline>
                                    ) : null}
                                </div>
                                {primaryComponent}
                            </div>
                        </div>
                    ) : (
                        <>{primaryComponent}</>
                    )}

                    {!hideSecondarySection ? (
                        <div className="secondary-section" id="secondary-section">
                            <div className="secondary-content" id="secondary-content">
                                <div className="section-header">
                                    <StandardSubtitle>{secondarySectionTitle}</StandardSubtitle>
                                </div>
                                {secondaryComponent}
                                <LegalPagesFooter />
                            </div>
                        </div>
                    ) : null}
                </Main>
            </Container>
            <MobileContainer>
                <Link href="/" passHref prefetch={false}>
                    <div className="logo">
                        <img src={IconDark.src} alt="Knowunity Logo" width={19} height={26} />
                        <Headline4 color={theme.colors.white}>Knowunity</Headline4>
                    </div>
                </Link>
                <FeatureNotAvailable />
                <GetInTouchSection />
            </MobileContainer>
        </Wrapper>
    )
}

export default AppContainer
